import {
  useCallback,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  TIMESTAMP_FORMATS,
  TimestampFormatContext,
  type TimestampFormat,
} from './TimestampFormatContext';
import { safeLocalStorage } from '@/lib/safeLocalStorage';

const STORAGE_KEY = 'ui.timestamp-format';

/** Narrows an arbitrary persisted value to a supported `TimestampFormat`. */
function isTimestampFormat(value: unknown): value is TimestampFormat {
  return TIMESTAMP_FORMATS.includes(value as TimestampFormat);
}

/**
 * Read the persisted format, validating against `TIMESTAMP_FORMATS` and
 * defaulting to `'absolute'` for a missing or corrupted value. Absolute is the
 * product default (KIR-3784): any user who has never explicitly chosen a format
 * — including existing users who never touched the setting — sees absolute
 * timestamps, while an explicit stored choice is always honored.
 */
const getSavedTimestampFormat = (): TimestampFormat => {
  const value = safeLocalStorage.getItem(STORAGE_KEY);
  return isTimestampFormat(value) ? value : 'absolute';
};

const saveTimestampFormat = (format: TimestampFormat): void => {
  safeLocalStorage.setItem(STORAGE_KEY, format);
};

/**
 * Client-side provider for the per-user Timestamp Format setting. Mirrors
 * `LayoutProvider`'s context→provider→hook shape (inline `safeLocalStorage`
 * helpers, synchronous `useState` seed so a reload shows no first-paint flash,
 * persist-then-update setter). No backend sync and no cross-tab storage
 * listener — the ticket explicitly permits client-side-only storage.
 */
export function TimestampFormatProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [value, setValueState] = useState<TimestampFormat>(() =>
    getSavedTimestampFormat()
  );

  const setValue = useCallback((next: TimestampFormat): void => {
    saveTimestampFormat(next);
    setValueState(next);
  }, []);

  return (
    <TimestampFormatContext.Provider value={{ value, setValue }}>
      {children}
    </TimestampFormatContext.Provider>
  );
}
